import {getDocumentMaxFiles} from '@sollapay/domain';
import {DocumentContext, DocumentEntityType, DocumentType, PackageType} from '@sollapay/enums';
import {ContentSection, EmptyState, Illustration} from '@sollapay/ui/components';
import {SecureDocumentIllustration} from '@sollapay/ui/illustrations';
import {formatFileSize, toISO} from '@sollapay/utils';
import {useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {
  AddDocumentsButton,
  buildCompanyDocumentTabs,
  DocumentsTable,
  getDocumentTypeLabel
} from '@/features/documents';
import {useActivationViewModel} from '@/features/trust-accounts/activation/hooks';
import {
  isPackageActionable,
  isPackagePendingReview
} from '@/features/trust-accounts/activation/utils';
import {
  trustAccountActivityKeys,
  useTrustAccountDocumentsQuery
} from '@/features/trust-accounts/hooks';
import {
  useCurrentTrustAccount,
  useTrustAccountPermissions
} from '@/features/trust-accounts/providers';

import {DocumentsTabContentSkeleton} from './DocumentsTabContent.skeleton';

import type {DocumentTableRow, DocumentUploadTab} from '@/features/documents';
import type {DocumentListItemDTO} from '@sollapay/types';
import type {FC} from 'react';

function mapDocumentDto(doc: DocumentListItemDTO): DocumentTableRow {
  const ext = doc.fileName.split('.').pop() ?? '';
  return {
    id: doc.id,
    fileName: doc.originalFileName ?? doc.fileName,
    fileExtension: ext,
    fileSize: formatFileSize(doc.fileSize),
    documentType: doc.documentType,
    uploadDate: toISO(doc.uploadedAt) || toISO(doc.createdAt),
    lastUpdated: toISO(doc.updatedAt),
    uploadedBy: doc.uploadedByUser?.name
      ? {
          name: doc.uploadedByUser.name,
          role: doc.uploadedByUser.role ?? '',
          avatarUrl: doc.uploadedByUser.pictureUrl
        }
      : undefined
  };
}

export const DocumentsTabContent: FC = () => {
  const {id: trustAccountId} = useCurrentTrustAccount();
  const {canUploadDocuments, canDeleteDocuments} = useTrustAccountPermissions();
  // Documents lock at the package level (canEdit), not the trust account's aggregate
  // status — a sibling package moving to IN_REVIEW/CLARIFICATION_REQUIRED must not lock
  // a package that's still actionable, matching ActivationPackageFormModal's derivation.
  const {packages} = useActivationViewModel(trustAccountId);
  const trustDetailsPackage = packages.find(p => p.type === PackageType.TRUST_DETAILS);
  const compliancePackage = packages.find(p => p.type === PackageType.COMPLIANCE);
  const isTrustDetailsEditable = isPackageActionable(trustDetailsPackage);
  const isComplianceEditable = isPackageActionable(compliancePackage);
  // Transaction legal documents relate to ongoing deals, not the activation workflow —
  // they stay addable/deletable whenever the compliance package isn't actively pending
  // an ops decision (including once it's approved), unlike the rest of compliance.
  const isTransactionLegalDocumentEditable = !isPackagePendingReview(compliancePackage);
  // Package state and role are independent gates — a package can be open while the role
  // still forbids the action, so both must hold.
  const canAddTrustDetailsDocs = canUploadDocuments && isTrustDetailsEditable;
  const canDeleteTrustDetailsDocs = canDeleteDocuments && isTrustDetailsEditable;
  const queryClient = useQueryClient();
  const documentsQuery = useTrustAccountDocumentsQuery(trustAccountId);
  const {t} = useTranslation(['trustAccounts', 'common']);
  const {data, isLoading, refetch} = documentsQuery;
  const onRefetch = () => {
    void refetch();
    void queryClient.invalidateQueries({
      queryKey: trustAccountActivityKeys.tabCounts(trustAccountId)
    });
  };
  const isComplianceRowDeletable = (row: DocumentTableRow) =>
    canDeleteDocuments &&
    (isComplianceEditable ||
      (isTransactionLegalDocumentEditable &&
        row.documentType === DocumentType.TRANSACTION_LEGAL_DOCUMENT));

  if (isLoading) {
    return <DocumentsTabContentSkeleton />;
  }

  const documentsEmptyState = (
    <EmptyState
      className="py-8"
      illustration={
        <Illustration width={64} height={64}>
          <SecureDocumentIllustration />
        </Illustration>
      }
      title={t('view.documents.emptyState.title')}
      subtitle={t('view.documents.emptyState.subtitle')}
    />
  );

  const trustDetailsTabs = buildCompanyDocumentTabs(t);

  const transactionLegalDocumentTab: DocumentUploadTab = {
    value: 'transactionLegalDocument',
    label: getDocumentTypeLabel(DocumentType.TRANSACTION_LEGAL_DOCUMENT, t),
    documentType: DocumentType.TRANSACTION_LEGAL_DOCUMENT,
    maxFiles: getDocumentMaxFiles(DocumentType.TRANSACTION_LEGAL_DOCUMENT)
  };
  const complianceTabs: DocumentUploadTab[] = [
    {
      value: 'landOwnershipVerification',
      label: getDocumentTypeLabel(DocumentType.LAND_OWNERSHIP_VERIFICATION, t),
      documentType: DocumentType.LAND_OWNERSHIP_VERIFICATION,
      required: true,
      maxFiles: getDocumentMaxFiles(DocumentType.LAND_OWNERSHIP_VERIFICATION)
    },
    transactionLegalDocumentTab
  ];
  // Once its package leaves the editable phase, only the transaction-legal tab stays
  // open for upload, and only while the compliance package isn't pending ops review.
  const packageComplianceTabs = isComplianceEditable
    ? complianceTabs
    : isTransactionLegalDocumentEditable
      ? [transactionLegalDocumentTab]
      : [];
  const complianceAddTabs = canUploadDocuments ? packageComplianceTabs : [];

  return (
    <div className="flex flex-col gap-10">
      <ContentSection
        title={t('view.documents.trustDetails.title')}
        subtitle={t('view.documents.trustDetails.subtitle')}
        isLoading={isLoading}
        action={
          canAddTrustDetailsDocs && (
            <AddDocumentsButton
              title={t('view.documents.trustDetails.title')}
              tabs={trustDetailsTabs}
              entityType={DocumentEntityType.TRUST_ACCOUNT}
              entityId={trustAccountId}
              documentContext={DocumentContext.TRUST_DETAILS}
              onDocumentUploaded={onRefetch}
              onDocumentChange={onRefetch}
            />
          )
        }
      >
        <DocumentsTable
          documents={(data?.trustDetails ?? []).map(mapDocumentDto)}
          entityType={DocumentEntityType.TRUST_ACCOUNT}
          entityId={trustAccountId}
          documentContext={DocumentContext.TRUST_DETAILS}
          emptyState={documentsEmptyState}
          onDeleted={canDeleteTrustDetailsDocs ? onRefetch : undefined}
        />
      </ContentSection>

      <ContentSection
        title={t('view.documents.compliance.title')}
        subtitle={t('view.documents.compliance.subtitle')}
        isLoading={isLoading}
        action={
          complianceAddTabs.length > 0 && (
            <AddDocumentsButton
              title={t('view.documents.compliance.title')}
              tabs={complianceAddTabs}
              entityType={DocumentEntityType.TRUST_ACCOUNT}
              entityId={trustAccountId}
              documentContext={DocumentContext.COMPLIANCE}
              onDocumentUploaded={onRefetch}
              onDocumentChange={onRefetch}
            />
          )
        }
      >
        <DocumentsTable
          documents={(data?.compliance ?? []).map(mapDocumentDto)}
          entityType={DocumentEntityType.TRUST_ACCOUNT}
          entityId={trustAccountId}
          documentContext={DocumentContext.COMPLIANCE}
          emptyState={documentsEmptyState}
          onDeleted={onRefetch}
          isRowDeletable={isComplianceRowDeletable}
        />
      </ContentSection>
    </div>
  );
};
